import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Building2,
  ChevronDown,
  ChevronUp,
  FileText,
  Activity,
  Settings,
  X,
} from "lucide-react";
import { BillingModeSettings } from "@/components/admin/BillingModeSettings";
import { UnbilledUsageSummary } from "@/components/admin/UnbilledUsageSummary";
import { UsageLogViewer } from "@/components/admin/UsageLogViewer";
import { UsagePricesEditor } from "@/components/admin/UsagePricesEditor";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import api from "@/services/api";
import { generateUUID } from "@/utils/transform";

export const OrganisationBillingPanel = ({
  organisationId,
  onClose,
  onCreateInvoice,
}) => {
  const [activeTab, setActiveTab] = useState("overview");
  const queryClient = useQueryClient();

  // Fetch organisation details.
  //
  // The endpoint wraps the row: { organisation, effectiveFeatures,
  // overrideLogs, members }, so the `.organisation` unwrap is required;
  // without it every field below reads undefined and silently falls back
  // (plan -> "free", billing_mode -> "stripe", name -> blank).
  //
  // Deliberately NOT keyed ["admin", "organisation", id]: Organizations.jsx
  // caches the whole envelope under that key and reads orgDetails.organisation
  // without optional chaining, so sharing it would white-screen that dialog.
  const { data: organisation, isLoading } = useQuery({
    queryKey: ["admin", "organisation-billing-panel", organisationId],
    queryFn: async () => {
      const response = await api.get(`/admin/organisations/${organisationId}`);
      return response.data?.data?.organisation;
    },
    enabled: !!organisationId,
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const billingMode =
    organisation?.billing_mode || organisation?.billingMode || "stripe";

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Building2 className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-foreground">
              {organisation?.name}
            </h2>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="outline">{organisation?.plan || "free"}</Badge>
              <Badge
                className={
                  billingMode === "invoice"
                    ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                    : "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200"
                }
              >
                {billingMode === "invoice"
                  ? "Invoice Billing"
                  : "Stripe Billing"}
              </Badge>
            </div>
          </div>
        </div>
        {onClose && (
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="overview" className="gap-2">
            <FileText className="h-4 w-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="usage" className="gap-2">
            <Activity className="h-4 w-4" />
            Usage Log
          </TabsTrigger>
          <TabsTrigger value="settings" className="gap-2">
            <Settings className="h-4 w-4" />
            Settings
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6 space-y-6">
          {/* Unbilled Usage Summary */}
          <UnbilledUsageSummary
            organisationId={organisationId}
            organisationName={organisation?.name}
            onCreateInvoice={onCreateInvoice}
          />
        </TabsContent>

        <TabsContent value="usage" className="mt-6">
          <UsageLogViewer
            organisationId={organisationId}
            organisationName={organisation?.name}
          />
        </TabsContent>

        <TabsContent value="settings" className="mt-6 space-y-6">
          <BillingModeSettings
            organisationId={organisationId}
            organisationName={organisation?.name}
            onUpdate={() =>
              queryClient.invalidateQueries({
                queryKey: ["admin", "organisation-billing-panel", organisationId],
              })
            }
          />
          <UsagePricesEditor
            organisationId={organisationId}
            organisationName={organisation?.name}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

/**
 * Dialog wrapper for OrganisationBillingPanel
 */
export const OrganisationBillingDialog = ({
  open,
  onOpenChange,
  organisationId,
  onCreateInvoice,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
      <OrganisationBillingPanel
        organisationId={organisationId}
        onClose={() => onOpenChange(false)}
        onCreateInvoice={(data) => {
          onCreateInvoice?.(data);
          onOpenChange(false);
        }}
      />
    </DialogContent>
  </Dialog>
);

/**
 * Invoice-billed organisations list for admin
 */
export const InvoiceBilledOrganisations = ({ onCreateInvoice }) => {
  const [expandedOrg, setExpandedOrg] = useState(null);

  // Fetch invoice-billed organisations
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "organisations-invoice-billed"],
    queryFn: async () => {
      const response = await api.get("/admin/organisations-invoice-billed");
      return response.data?.data;
    },
  });

  const organisations = data?.items || [];

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[...Array(3)].map(() => (
          <Skeleton key={generateUUID()} className="h-20 w-full" />
        ))}
      </div>
    );
  }

  if (organisations.length === 0) {
    return (
      <Card className="border-border">
        <CardContent className="py-12 text-center">
          <Building2 className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
          <p className="text-muted-foreground">
            No organisations set up for invoice billing yet
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            Switch an organisation to invoice billing from their billing
            settings
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {organisations.map((org) => (
        <Collapsible
          key={org.id}
          open={expandedOrg === org.id}
          onOpenChange={(open) => setExpandedOrg(open ? org.id : null)}
        >
          <Card className="border-border">
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Building2 className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <CardTitle className="text-base font-medium text-foreground">
                        {org.name}
                      </CardTitle>
                      <p className="text-xs text-muted-foreground">
                        {org.billing_email ||
                          org.billingEmail ||
                          "No billing email"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant="outline">{org.plan || "free"}</Badge>
                    <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                      Net {org.payment_terms_days || org.paymentTermsDays || 14}
                    </Badge>
                    {expandedOrg === org.id ? (
                      <ChevronUp className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                </div>
              </CardHeader>
            </CollapsibleTrigger>

            <CollapsibleContent>
              <CardContent className="pt-0 pb-4">
                <div className="border-t border-border pt-4">
                  <OrganisationBillingPanel
                    organisationId={org.id}
                    onCreateInvoice={onCreateInvoice}
                  />
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      ))}
    </div>
  );
};

export default OrganisationBillingPanel;
